import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  NumberInputField,
  NumberInput,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormErrorMessage,
  Text,
  Link,
  Flex,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { useCallback } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormMainContainer } from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { CauseContributionPointsAllocationSchema } from '@duke-hq/svc-api/provider/trpc/routers/tenant/groupRewards/schemas';

type ContributionSectionProps = {
  pointsAvailable: number;
  onContribute: (amount: number) => void;
  showPointsAvailable?: boolean;
  buttonText?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  width?: string;
  margin?: string | number;
  isDisabled?: boolean;
};

export function ContributionSection({
  pointsAvailable,
  onContribute,
  showPointsAvailable = true,
  buttonText = 'Contribute',
  buttonSize = 'lg',
  isLoading = false,
  width,
  margin = 6,
}: ContributionSectionProps) {
  const methods = useForm<{
    targetAmount: number;
  }>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(CauseContributionPointsAllocationSchema),
    defaultValues: {
      targetAmount: 0,
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const currentAmount = watch('targetAmount') || 0;
  const remainingPoints = pointsAvailable - currentAmount;

  const handleMaxClick = useCallback(() => {
    setValue('targetAmount', pointsAvailable);
  }, [setValue, pointsAvailable]);

  const onSubmit = (data: { targetAmount: number }) => {
    const numericAmount = data.targetAmount;
    if (!isNaN(numericAmount) && numericAmount > 0) {
      onContribute(numericAmount);
    }
  };

  return (
    <Box my={margin} width={width}>
      <FormProvider {...methods}>
        <Box as="form" onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }} noValidate>
          <FormMainContainer>
            <FormControl isRequired isInvalid={!!errors.targetAmount}>
              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel htmlFor="CauseForm-targetAmount">
                  <Trans>Contribute amount</Trans>
                </FormLabel>
                {showPointsAvailable != null && (
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="fg.muted">
                      <Trans>Points available:</Trans> {remainingPoints ?? 0}
                    </Text>
                    <Link
                      fontSize="sm"
                      color={pointsAvailable <= 0 ? 'gray.400' : 'blue.500'}
                      textDecoration="none"
                      cursor={pointsAvailable <= 0 ? 'not-allowed' : 'pointer'}
                      onClick={pointsAvailable > 0 ? handleMaxClick : undefined}
                    >
                      <Trans>(Max)</Trans>
                    </Link>
                  </HStack>
                )}
              </Flex>
              <Controller
                name="targetAmount"
                control={control}
                render={({ field }) => {
                  const maxAllowable = pointsAvailable;
                  return (
                    <NumberInput
                      min={1}
                      max={maxAllowable}
                      precision={2}
                      step={1}
                      isDisabled={pointsAvailable <= 0}
                      onChange={(_, valueAsNumber) => {
                        if (isNaN(valueAsNumber)) {
                          field.onChange(0);
                        } else if (valueAsNumber > maxAllowable) {
                          field.onChange(maxAllowable);
                        } else {
                          field.onChange(valueAsNumber);
                        }
                      }}
                      value={isNaN(field.value) ? '' : field.value}
                    >
                      <NumberInputField id="CauseForm-targetAmount" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  );
                }}
              />
              <FormErrorMessage>{errors.targetAmount?.message}</FormErrorMessage>
            </FormControl>
            <Button
              size={buttonSize}
              w="full"
              py={6}
              mt={4}
              type="submit"
              isLoading={isSubmitting || isLoading}
              isDisabled={pointsAvailable <= 0 || currentAmount === 0}
            >
              {buttonText}
            </Button>
          </FormMainContainer>
        </Box>
      </FormProvider>
    </Box>
  );
}
